import { useEffect, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

import { IDeal, IDealRealtor, IPurchaserInfo } from '../../types/CreateDealForm';
import TextEditor from '../common/textEditor/TextEditor';
import { selectProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar, showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import LoadingWrapper from '../common/LoadingWrapper';
import { camelToTitle, convertAllDates, numToCurrency } from '../../utils/Functions';

const Email = (props: ChildProps) => {
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();

  const { deal, realtor, deals } = props;
  const [newTemplate, setNewTemplate] = useState<string>();
  const [subject, setSubject] = useState<string>('');
  const [senderName, setSenderName] = useState<string>('');
  const [senderEmail, setSenderEmail] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [cc, setCc] = useState<string>(project ? project.email : '');

  useEffect(() => {
    setSubject(`${project.name} Suite ${deal.unit.suite} - Outstandings`);
    if (realtor) {
      setTo(realtor.email!);
    } else {
      setTo(deal?.purchasers.map((purchaser: IPurchaserInfo) => purchaser.email).join(', '));
    }
  }, []);

  const [addComment] = useMutation(ADDCOMMENT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [sendEmail, { loading }] = useLazyQuery(SENDEMAIL, {
    onCompleted: () => {
      let newObject = {
        project: project._id,
        unit: deal.unit?._id,
        deal: deal._id,
        user: user._id,
        comment: `Email has been sent to ${to}`,
        type: 'general',
        pinned: false,
        deleted: false,
      };
      addComment({ variables: { record: newObject } });
      storeDispatch(showSuccessSnackbar('Email has been sent!'));
    },
    onError: (e) => {
      storeDispatch(showErrorSnackbar('Sending Error. Please double check if your Google Account is connected to the app.'));
    },
  });

  const handleUserChange = (e: any) => {
    setSenderEmail(e.target.value);
    setSenderName(project?.name!);
  };

  const handleSendEmail = async (content: string, index: number) => {
    await sendEmail({
      variables: {
        to,
        html: content,
        subject,
        senderName,
        senderEmail,
        cc,
        project: project?._id,
        attachmentsSaved: [],
        attachmentsUnsaved: [],
      },
    });
  };

  const handleSendOutstandings = async (editor: any) => {
    let emailBody = '';
    let fullNames = '';
    if (realtor) {
      for (const deal of deals) {
        let dealBody = `<br><br><strong>Suite ${deal.unit.suite}</strong><br>`;
        let mortgageAmount = '';
        let deposits = deal.deposit;
        if (deal.pendingDeposits.length) {
          deposits = deal.pendingDeposits;
        }
        if (
          !deal.mortgage.lender ||
          (!deal.mortgage.amount && deal.mortgage.amount !== 0) ||
          (deal.mortgage.revisions.length !== 0 && !deal.mortgage.revisions.includes('No MPA Required'))
        ) {
          let sum = deposits.reduce(function (s: any, a: any) {
            return s + a.amount;
          }, 0);
          let totalOptions = deal.options.reduce((a: any, b: any) => {
            return a + b.amount;
          }, 0);
          mortgageAmount = `<br>Mortgage Amount Required: ${numToCurrency.format(deal.basePrice + totalOptions - sum)}<br>`;
          dealBody += mortgageAmount;
        }
        for (const deposit of deposits) {
          if (
            (deposit.chequeType === '' || deposit.chequeType === 'replacement' || deposit.chequeType === 'nsf') &&
            deposit.name !== 'Occupancy' &&
            ((deposit.dateType && deposit.dateType !== 'Floating Date') || !deposit.dateType)
          ) {
            dealBody += `<br>${deposit.name} - ${numToCurrency.format(deposit.amount!)} - ${convertAllDates(deposit.dueDate, 'PP')} - ${
              deposit.chequeType ? camelToTitle(deposit.chequeType) : 'Not Received'
            }`;
          }
        }
        emailBody += dealBody;
      }
      fullNames = realtor.fullName;
      setSubject(`${project.name} - ${fullNames} All Outstandings`);
    } else {
      let dealBody = `<br><br><strong>Suite ${deal.unit.suite}</strong><br>`;
      let mortgageAmount = '';
      let deposits = deal.deposit;
      if (deal.pendingDeposits.length) {
        deposits = deal.pendingDeposits;
      }
      if (
        !deal.mortgage.lender ||
        (!deal.mortgage.amount && deal.mortgage.amount !== 0) ||
        (deal.mortgage.revisions.length !== 0 && !deal.mortgage.revisions.includes('No MPA Required'))
      ) {
        let sum = deposits.reduce(function (s: any, a: any) {
          return s + a.amount;
        }, 0);
        let totalOptions = deal.options.reduce((a: any, b: any) => {
          return a + b.amount;
        }, 0);
        mortgageAmount = `<br>Mortgage Amount Required: ${numToCurrency.format(deal.basePrice + totalOptions - sum)}<br>`;
        dealBody += mortgageAmount;
      }
      for (const deposit of deposits) {
        if (
          (deposit.chequeType === '' || deposit.chequeType === 'replacement' || deposit.chequeType === 'nsf') &&
          deposit.name !== 'Occupancy' &&
          ((deposit.dateType && deposit.dateType !== 'Floating Date') || !deposit.dateType)
        ) {
          dealBody += `<br>${deposit.name} - ${numToCurrency.format(deposit.amount!)} - ${convertAllDates(deposit.dueDate, 'PP')} - ${
            deposit.chequeType ? camelToTitle(deposit.chequeType) : 'Not Received'
          }`;
        }
      }
      emailBody += dealBody;
      fullNames = deal.purchasers.map((purchaser: IPurchaserInfo) => purchaser.fullName).join(' & ');
    }
    let signature = `Best Regards,<br>${project.name} Team<br><br>Office Hours: By Appointment Only<br>Email: ${project.email}`;
    let email = `<p style="display: block; margin-block-start: 0; margin-block-end: 0; line-height: 1.5; margin-left: 0px!important; text-indent: 0pt;">Hello ${fullNames},<br><br>Please see below for your list of outstandings at ${project.name}.${emailBody}<br><br>${signature}</p>`;
    editor?.commands.setContent(email);
  };

  return !loading ? (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <TextField fullWidth required label="Subject" variant="outlined" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel required id="demo-simple-select-label">
            Sender Email
          </InputLabel>
          <Select fullWidth required label={'Sender Email'} value={senderEmail} onChange={(e) => handleUserChange(e)}>
            <MenuItem value={project?.email!}>
              {project?.name!} ({project?.email!})
            </MenuItem>
            <MenuItem value={user?.email}>
              {user?.fullName} ({user?.email})
            </MenuItem>
            {user?.emailAliases && user?.emailAliases.length
              ? user.emailAliases.map((email: string) => {
                  return <MenuItem value={email}>{email}</MenuItem>;
                })
              : null}
          </Select>
        </FormControl>
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          required
          label="Sender Name"
          variant="outlined"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
        />
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          required
          label="To: (Comma Separated)"
          variant="outlined"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <TextField
          sx={{ mt: 2 }}
          fullWidth
          required
          label="Cc: (Comma Separated)"
          variant="outlined"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
        />
      </Box>
      <TextEditor
        renderButtons={[
          { name: `Send Email`, function: handleSendEmail },
          { name: `All Outstandings Template`, function: handleSendOutstandings, append: true },
        ]}
        initContent={newTemplate}
        index={0}
        appendContent={newTemplate}
      />
    </Box>
  ) : (
    <LoadingWrapper title="Sending Email" modal={false} />
  );
};

interface ChildProps {
  deal: IDeal;
  deals: IDeal[];
  realtor: IDealRealtor | null;
}

const SENDEMAIL = gql`
  query sendEmail(
    $to: String!
    $subject: String!
    $senderName: String!
    $senderEmail: String!
    $cc: String
    $html: String!
    $project: MongoID!
    $attachmentsSaved: [AttachmentsSavedInput]!
    $attachmentsUnsaved: [Upload]!
  ) {
    sendPersonalizedStepEmail(
      to: $to
      subject: $subject
      senderName: $senderName
      senderEmail: $senderEmail
      cc: $cc
      html: $html
      project: $project
      attachmentsSaved: $attachmentsSaved
      attachmentsUnsaved: $attachmentsUnsaved
    ) {
      name
    }
  }
`;

const ADDCOMMENT = gql`
  mutation commentCreateOne($record: CreateOneCommentInput!) {
    commentCreateOne(record: $record) {
      record {
        _id
        type
        pinned
        comment
        deal {
          _id
        }
        updatedAt
        createdAt
        user {
          fullName
        }
      }
    }
  }
`;

export default Email;
